import React, { Fragment, useEffect, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import Switch from "react-switch";
import Breadcrumb from "../../../layout/breadcrumb";
import PreLoader from "../../../share-components/ui/preLoader";
import { DatePicker } from "rsuite";
import AsyncSelect from "react-select/async";
import ModalFormSubmit from "../../../share-components/form/modalFormSubmit";
import { closeLookBookDialog, openLookBookDialog, selectProduct } from "../../manage-product/store/prodcutSlice";
import { useDispatch, useSelector } from "react-redux";
import CountriesModalWindow from "../content/CountriesModal";
import ProductTable from "../content/ProductTable";
import discountReducer from "../store";
import withReducer from "../../../store/withReducer";
import {
  closeCountryDialog,
  closeDiscountDialog,
  closeProductsDialog,
  editPromotionDiscount,
  getDiscountSettings,
  getPromotionSettings,
  openCountryDialog,
  openDiscountDialogs,
  openProductsDialog,
  setDiscountSettings,
  setModalData,
  setSettings,
  storePromotionDiscount,
  updatePromotionDiscount,
} from "../store/discountSlice";
import DiscountModal from "../content/DiscountModal";
import CommonModal from "../../../share-components/ui/commonModal";
import moment from "moment";
import ShippingRates from "../content/ShippingRates";
import DiscountProductSearchWindow from "../content/DiscountProductSearch";

const promotionType = [
  { id: 1, name: "Buy 1 get 1" },
  { id: 2, name: "Buy X get Y" },
  { id: 3, name: "Amount off product" },
  { id: 4, name: "Amount off order" },
  { id: 5, name: "Free shipping" },
];

const defaultValues = {
  discount_title: "",
  discount_type: 1,
  is_active: true,
  buy_qty: "",
  get_qty: "",
  mpr_type: 1,
  discount_value: 1,
  customer_spends: 2,
  discounted_value: 1,
  country_options: 1,
  start_date: null,
  expiry_date: null,
  buy_products: [],
  get_products: [],
  combinations: [],
  selected_countries: [],
  shipping_countries: [],
  maximum_shipping_price: "",
  country_status: 1,
  shipping_rates_checked: false,
  shipping_rates: "",
};

const defaultValuesCountry = {
  selectedCountries: [],
};

const schema = yup.object().shape({
  discount_title: yup.string().required("Discount Title is a required field"),
  discount_type: yup.string().required("Discount Type is required"),
  start_date: yup
    .date()
    .nullable()
    .required("Start Date is required")
    .test("start_before_expiry", "Start Date cannot be after Expiry Date", function (value) {
      const { expiry_date } = this.parent;
      if (!value || !expiry_date) return true;
      return value <= expiry_date;
    }),
  expiry_date: yup.date().nullable().required("Expire Date is required"),
  buy_qty: yup.mixed().when("discount_type", {
    is: (val) => val !== "2" && val !== "3" && val !== "4" && val !== "5",
    then: yup
      .number()
      .typeError("Buy Qty must be a number")
      .integer("Buy Qty must be a whole number")
      .required("Buy Qty is required for this discount type"),
    otherwise: yup.mixed().nullable(),
  }),
  get_qty: yup
    .mixed()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when("discount_type", (discount_type, schema) => {
      if (["3", "4", "5"].includes(discount_type)) {
        // For 3, 4, 5 → allow null
        return yup.mixed().nullable().typeError("Get Qty must be a number");
      }

      if (discount_type === "2") {
        // For 2 → required + whole number
        return yup
          .number()
          .required("Get Qty is required for this discount type")
          .typeError("Get Qty must be a number")
          .integer("Get Qty must be a whole number");
      }
      // For others → must be an integer
      return yup.number().nullable().integer("Get Qty must be a whole number").typeError("Get Qty must be a number");
    }),
  amount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Amount must be a number")
    .when(["discount_type", "discount_value"], (discount_type, discount_value, schema) => {
      if (["3", "4"].includes(discount_type) && discount_value === 1) {
        return schema.required("Amount is required");
      }
      return schema;
    }),
  minimum_quantity: yup
    .mixed()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when(["discount_type", "customer_spends"], (discount_type, customer_spends, schema) => {
      if (discount_type === "2" && customer_spends === 2) {
        return yup
          .number()
          .required("Minimum Quantity is required for this discount type")
          .typeError("Minimum Quantity must be a number")
          .integer("Minimum Quantity must be a whole number");
      }
      // For other cases, allow null or decimal
      return yup.number().nullable();
    }),
  percentage: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Percentage must be a number")
    .when(["discount_type", "discount_value"], (discount_type, discount_value, schema) => {
      if (["3", "4"].includes(discount_type) && discount_value === 2) {
        return schema.required("Percentage is required");
      }
      return schema;
    }),

  mpr_type: yup.number().nullable(),
  selected_countries: yup.array().when("country_options", {
    is: 2, // When "Selected Countries" is chosen
    then: yup.array().min(1, "Please select at least one country"),
  }),
  minimum_items: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Minimum Quantity must be a number")
    .integer("Minimum Quantity must be a whole number")
    .min(0, "Minimum Quantity cannot be less than 0"),
});

const schemaCountry = yup.object().shape({
  selectedCountries: yup.array().of(
    yup.object().shape({
      code: yup.string().required("Country code is required"),
      name: yup.string().required("Country name is required"),
    })
  ),
});

const schemaProdcuts = yup.object().shape({
  buy_products: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.number().required("Buy Product ID is required"),
      })
    )
    .min(1, "At least one buy product must be selected."),
});

function DiscountForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(1);
  const [status, setStatus] = useState(1);
  const [minimumPurchaseStatus, setMinimumPurchaseStatus] = useState(0);
  const [discountedStatus, setDiscountedStatus] = useState(0);
  const [countryStatus, seCountryStatus] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [combinationsList, setCombinationList] = useState([]);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [getSearchTerm, setGetSearchTerm] = useState("");
  const [isActive, setIsActive] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [checkedPromotionDiscount, setCheckedPromotionDiscount] = useState(false);
  const [checkedOrderDiscount, setCheckedOrderDiscount] = useState(false);
  const [checkedShippingDiscount, setCheckedShippingDiscount] = useState(false);
  const [shippingRates, setShippingRates] = useState(false);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [openCountryModal, setOpenCountryModal] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isGet, setIsGet] = useState(false);
  const product = useSelector(selectProduct);
  const discountState = useSelector(({ discount }) => discount.discount);
  const [searchSkuTerm, setSearchSkuTerm] = useState("");
  const [getSearchSkuTerm, setGetSearchSkuTerm] = useState("");

  const { handleSubmit, formState, reset, control, register, setValue, trigger, getValues, watch } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { errors, isDirty, isSubmitting } = formState;

  const {
    handleSubmit: handleSubmitCountry,
    formState: formCountry,
    reset: resetCountry,
    append: appendCountry,
    watch: watchCountry,
    setValue: setCountryValues,
    getValues: getCountryValues,
    control: controlCountry,
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValuesCountry,
    resolver: yupResolver(schemaCountry),
  });

  const errorsCountry = formCountry.errors;
  console.log("errors", errors);

  const buyQty = watch("buy_qty");
  const discountType = watch("discount_type");

  // Keep get_qty in sync with buy_qty when status === 1
  useEffect(() => {
    if (status === 1) {
      setValue("get_qty", Number(buyQty) || "");
    }
  }, [buyQty, status, setValue]);

  const discountTypeGlobe = watch("discount_type");
  console.log("id",id)

  useEffect(() => {
    dispatch(getPromotionSettings())
      .then((res) => {
        dispatch(setSettings(res.payload));
        setLoading(false);
      })
      .catch(() => {
        console.error("Failed to load promotion settings");
        setLoading(false);
      });
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(editPromotionDiscount(id))
        .then((res) => {
          const data = res.payload;

          const alignedData = {
            discount_title: data.title,
            discount_type: data.type,
            is_active: data.is_active || false,
            buy_qty: data.mpr_type === 1 ? 0 : parseInt(data.buy_qty),
            get_qty: data.discount_value && data.type !== 2 ? 1 : data.get_qty,
            start_date: data.start_date ? new Date(data.start_date) : null,
            expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
            buy_products: data.buy_products || [],
            get_products: data.get_products || [],
            discount_value: data.discount_value || null,
            mpr_type: data.mpr_type || null,
            minimum_purchase: data.mpr_type === 3 ? data.buy_qty : null,
            minimum_items: data.mpr_type === 2 ? data.buy_qty : null,
            amount: data.discount_value === 1 ? data.get_qty : null,
            percentage: data.discount_value === 2 ? data.get_qty : null,
            customer_spends: data.type === 2 ? data.mpr_type : null,
            minimum_amount: data.type === 2 && data.mpr_type === 3 ? data.buy_qty : null,
            minimum_quantity: data.type === 2 && data.mpr_type === 2 ? data.buy_qty : null,
            discounted_amount: data.type === 2 && data.discount_value === 1 ? data.discounted_value : null,
            discounted_percentage: data.type === 2 && data.discount_value === 2 ? data.discounted_value : null,
            shipping_rates: data.type === 5 && data.maximum_shipping_price,
            maximum_shipping_price: data.type === 5 && data.maximum_shipping_price,
            shipping_rates_checked: Number(data.maximum_shipping_price) > 0.0,
            shipping_countries: data.shipping_countries,
          };

          setStartDate(data.start_date ? new Date(data.start_date) : null);
          setEndDate(data.expiry_date ? new Date(data.expiry_date) : null);

          setValue("discount_type", data.type);
          setStatus(data.type);

          setShippingRates(alignedData.shipping_rates);

          reset(alignedData);

          if (data.shipping_countries && data.shipping_countries.length > 0) {
            setValue("country_status", 2);
            seCountryStatus(1);
          } else {
            setValue("country_status", 1);
            seCountryStatus(0);
          }

          if (data.combinations.some((comb) => comb.id === 1)) setValue("product_discount", true);
          if (data.combinations.some((comb) => comb.id === 2)) setValue("order_discount", true);
          if (data.combinations.some((comb) => comb.id === 3)) setValue("shipping_discount", true);
          setLoading(false);
        })
        // .catch((err) => {
        //   console.log("CATCH ERROR 👉", err);
        //   history.push("/discounts");
        // });
    } else {
      setLoading(false);
    }
  }, [dispatch, id, reset, history]);

  useEffect(() => {
    setValue("buy_products", []);
    setValue("get_products", []);
    setSearchTerm("");
    setGetSearchTerm("");
    setSearchSkuTerm("");
    setGetSearchSkuTerm("");
  }, [discountType, setValue]);

  function handleSelectCountries(countries) {
    setSelectedCountries(countries);
    setValue("selected_countries", countries);
  }

  const getBuyQty = (data) => {
    if (data.discount_type === "4" || data.discount_type === "5" || data.discount_type === "3") {
      if (data.mpr_type === 1) return 0;
      return data.minimum_purchase ? parseFloat(data.minimum_purchase) : Number(data.minimum_items) || 0;
    }
    if (data.discount_type === "2") {
      return data.customer_spends === 2 ? data.minimum_quantity : data.minimum_amount;
    }
    return data.buy_qty;
  };

  const getGetQty = (data) => {
    if (data.discount_type === "6") return 0;
    return data.amount ? parseFloat(data.amount) : data.percentage ? parseFloat(data.percentage) : parseFloat(data.get_qty);
  };

  const onSubmitForm = (data) => {
    setSubmitLoading(true);
    let combinationsList = [];

    const updateCombination = (flag, value) => {
      if (flag) {
        if (!combinationsList.includes(value)) combinationsList.push(value);
      } else {
        combinationsList = combinationsList.filter((item) => item !== value);
      }
    };

    updateCombination(data.product_discount, 1);
    updateCombination(data.order_discount, 2);
    updateCombination(data.shipping_discount, 3);

    let countryCode = [];

    if (data.discount_type === "5") {
      if (countryStatus !== 1) {
        countryCode = [];
      } else {
        countryCode = getValues("shipping_countries");
      }
    }

    const payload = {
      title: data.discount_title,
      type: data.discount_type,
      buy_qty: getBuyQty(data),
      get_qty: getGetQty(data),
      start_date: moment(data.start_date).format("YYYY-MM-DD HH:mm:ss"),
      expiry_date: moment(data.expiry_date).format("YYYY-MM-DD HH:mm:ss"),
      is_active: data.is_active,
      buy_product_variant_ids: data.buy_products.map((product) => product.product_variant_id),
      ...(data.discount_type !== "1" ? { get_product_variant_ids: data.get_products.map((product) => product.product_variant_id) } : {}),

      ...(data.discount_type === "2" ? { mpr_type: data.customer_spends ?? 2 } : {}),
      ...(data.discount_type === "1" && { mpr_type: 2 }),
      ...(data.discount_type === "4" || data.discount_type === "5" || data.discount_type === "6" || data.discount_type === "3"
        ? { mpr_type: data.mpr_type ?? 1 }
        : {}),
      ...(data.discount_type === "1" && { discount_value: 3 }),
      ...(["2", "3", "4", "5"].includes(data.discount_type) && { discount_value: data.discount_value }),

      ...(data.discount_type === "2" && data.discount_value === 1 && { discounted_value: data.discounted_amount }),
      ...(data.discount_type === "2" && data.discount_value === 2 && { discounted_value: data.discounted_percentage }),
      // ...(data.discount_value !== undefined && { discount_value: data.discount_value }),
      ...(data.country_options === 2
        ? {
            country_codes: data.selected_countries.map((country) => country.code),
          }
        : {}),
      ...(data.discount_type === "5" && data.shipping_rates && { maximum_shipping_price: data.shipping_rates }),
      ...(data.discount_type === "5" && { shipping_countries: countryCode }),
      combinations: combinationsList.length > 0 ? combinationsList : [],
    };

    if (id) {
      dispatch(updatePromotionDiscount({ ...payload, id })).then((res) => {
        setSubmitLoading(false);
        if (res.payload.status === true) {
          history.push("/discounts");
        }
      });
    } else {
      dispatch(storePromotionDiscount(payload)).then((res) => {
        setSubmitLoading(false);
        if (!res.error) {
          history.push("/discounts");
        }
      });
    }
  };

  const handleSearchChange = (event) => {
    let obj = {
      props: { isOpen: true },
    };
    setSearchTerm(event.target.value);
    dispatch(openProductsDialog(obj));
    setIsGet(false);
  };
  const handleSearchSkuChange = (event) => {
    let obj = {
      props: { isOpen: true },
    };
    setSearchSkuTerm(event.target.value);
    dispatch(openProductsDialog(obj));
    setIsGet(false);
  };

  const handleGetSearchChange = (event) => {
    let obj = {
      props: { isOpen: true },
    };
    setGetSearchTerm(event.target.value);
    dispatch(openProductsDialog(obj));
    setIsGet(true);
  };
  const handleGetSearchSkuChange = (event) => {
    let obj = {
      props: { isOpen: true },
    };
    setGetSearchSkuTerm(event.target.value);
    dispatch(openProductsDialog(obj));
    setIsGet(true);
  };

  const handleAddProduct = (newProducts) => {
    const discountType = getValues("discount_type");

    if (discountType === 2) {
      const buyProducts = newProducts.buy || [];
      const getProducts = newProducts.get || [];

      const uniqueBuyProducts = Array.from(new Map(buyProducts.map((prod) => [prod.product_variant_id, prod])).values());
      const uniqueGetProducts = Array.from(new Map(getProducts.map((prod) => [prod.product_variant_id, prod])).values());

      reset({
        ...getValues(),
        buy_products: uniqueBuyProducts,
        get_products: uniqueGetProducts,
      });
    } else {
      const buyProducts = newProducts.buy || [];
      const uniqueBuyProducts = Array.from(new Map(buyProducts.map((prod) => [prod.product_variant_id, prod])).values());

      reset({
        ...getValues(),
        buy_products: uniqueBuyProducts,
        get_products: [],
      });
    }
  };

  const handleChangePromotion = (e) => {
    if (e.target.checked) {
      if (!combinationsList.includes(1)) {
        setCombinationList((prev) => [...prev, 1]);
      }
    } else {
      const updatedCombinations = combinationsList.filter((val) => val !== 1);
      setCombinationList(updatedCombinations);
    }
    setCheckedPromotionDiscount((prev) => !prev);
  };

  const handleChangeOrder = (e) => {
    if (e.target.checked) {
      if (!combinationsList.includes(2)) {
        setCombinationList((prev) => [...prev, 2]);
      }
    } else {
      const updatedCombinations = combinationsList.filter((val) => val !== 2);
      setCombinationList(updatedCombinations);
    }
    setCheckedOrderDiscount((prev) => !prev);
  };

  const handleChangeFreeShipping = (e) => {
    if (e.target.checked) {
      if (!combinationsList.includes(3)) {
        setCombinationList((prev) => [...prev, 3]);
      }
    } else {
      const updatedCombinations = combinationsList.filter((val) => val !== 3);
      setCombinationList(updatedCombinations);
    }
    setCheckedShippingDiscount((prev) => !prev);
  };

  function handleOpenDiscountModal(discountType) {
    let obj = {
      props: { isOpen: true },
    };

    if (discountType === "products") {
      obj = {
        ...obj,
        title: "Discount can combine with these product discounts",
      };
      dispatch(setModalData(discountState.settings?.combinedDiscounts?.product_discount?.data));
    } else if (discountType === "order") {
      obj = { ...obj, title: "Discount can combine with these order discounts" };
      dispatch(setModalData(discountState.settings?.combinedDiscounts?.order_discount?.data));
    } else if (discountType === "shipping") {
      obj = { ...obj, title: "Discount can combine with these shipping discounts" };
      dispatch(setModalData(discountState.settings?.combinedDiscounts?.shipping_discount?.data));
    }
    dispatch(openDiscountDialogs(obj));
  }

  function handleCloseDiscountModal() {
    setOpenDiscountModal(false);
    dispatch(setModalData([]));
  }

  const discountValue = [
    { value: 1, label: "Amount" },
    { value: 2, label: "Percentage" },
  ];

  const buyQuantityOptions = [
    { value: 2, label: "Minimum Purchase Quantity" },
    { value: 3, label: "Minimum Purchase Amount" },
  ];

  const loadOptions = (inputValue, callback) => {
    const filteredOptions = discountValue.filter((seller) => seller.label.toLowerCase().includes(inputValue.toLowerCase()));
    callback(filteredOptions);
  };

  const buyQyantityloadOptions = (inputValue, callback) => {
    const filteredOptions = buyQuantityOptions.filter((seller) => seller.label.toLowerCase().includes(inputValue.toLowerCase()));
    callback(filteredOptions);
  };

  function handleChangeShippingRates(e) {
    setShippingRates(e.target.checked);
    setValue("shipping_rates", e.target.checked);
  }

  function handleOpenCountryModal(discountType) {
    setOpenCountryModal(true);
  }

  const dataMenu = [
    {
      type: 1,
      text: "Discounts",
      link: "/discounts",
    },
    {
      type: 0,
      text: "New Discount",
    },
  ];

  const minimumPurchaseRequirement = [
    {
      id: 1,
      name: "No minimum requirements",
    },
    {
      id: 3,
      name: "Minimum purchase amount(£)",
    },
    {
      id: 2,
      name: "Minimum quanity of items",
    },
  ];

  const discountedValueOptions = [
    {
      id: 3,
      name: "Free",
    },
    {
      id: 2,
      name: "Percentage",
    },
    {
      id: 1,
      name: "Amount off each",
    },
  ];

  const countryeOptions = [
    {
      id: 1,
      name: "Free",
    },
    {
      id: 2,
      name: "All countries",
    },
  ];

  const discountValueOptions = [
    { id: 1, name: "Amount" },
    { id: 2, name: "Percentage" },
  ];

  const onAdd = () => {};

  const closeLookDialog = () => {
    dispatch(closeCountryDialog());
  };
  const closeDiscountWindowDialog = () => {
    dispatch(closeDiscountDialog());
  };
  const clearValues = () => {
    setSearchTerm("");
    setGetSearchTerm("");
    setSearchSkuTerm("");
    setGetSearchSkuTerm("");
  }
  const closeProductWindowDialog = () => {
    setSearchTerm("");
    setGetSearchTerm("");
    dispatch(closeProductsDialog());
  };

  const onSubmitCountryForm = (dialog, data) => {
    // console.log("dialog:", dialog);
    // console.log("data:", data);
    // console.log("Submitted countries:", data.selectedCountries);
    setValue("selected_countries", data.selectedCountries);
    const selecteCntryCode = data.selectedCountries.map((cntry) => cntry.code);
    setValue("shipping_countries", selecteCntryCode);
    dispatch(closeCountryDialog());
  };

  const onFormSubmit = (data) => {};


  return (
    <Fragment>
      <Breadcrumb title={id ? "Update Discount" : "New Discount"} data={dataMenu} />
      <Container fluid={true} className="product-wrapper">
        {loading ? (
          <>
            <PreLoader></PreLoader>
          </>
        ) : (
          <>
            <div className="product-grid">
              <div className="feature-products discount-specification">
                <Form onSubmit={handleSubmit(onSubmitForm)}>
                  <Card className="p-4">
                    <Row className="top-row">
                      <Col sm="12" className="discount-lists">
                        <Nav tabs className="border-tab">
                          {promotionType.map((item, index) => {
                            const isActive = watch("discount_type") === item.id;
                            const isDisabled = !!id;

                            return (
                              <NavItem key={item.id} className={`${isActive ? "flex-active" : ""} ${isDisabled ? "disabled" : ""}`}>
                                <NavLink
                                  className={watch("discount_type") === item.id ? "active" : ""}
                                  onClick={() => {
                                    if (isDisabled || submitLoading) return;
                                    setStatus(item.id);
                                    setValue("discount_type", item.id);
                                  }}
                                >
                                  {item.name}
                                </NavLink>
                              </NavItem>
                            );
                          })}
                        </Nav>
                      </Col>
                    </Row>

                    <Row className="second-row">
                      <Col sm={12} md={6} lg={6} className="second-col">
                        <Controller
                          name="discount_title"
                          control={control}
                          render={({ field }) => (
                            <Input {...field} bsSize="md" type="text" placeholder="Discount name" invalid={!!errors.discount_title} disabled={submitLoading}/>
                          )}
                        />
                        <div className="text-danger">{errors.discount_title ? errors.discount_title.message : ""}</div>
                      </Col>
                      <Col sm={12} md={6} lg={3} className="second-col">
                        <DatePicker
                          name="start_date"
                          placeholder="Discount start date"
                          value={startDate}
                          style={{ padding: 0 }}
                          className={`form-control digits ${errors.start_date ? "is-invalid" : ""}`}
                          showTime
                          format="dd-MM-yyyy HH:mm"
                          disabledDate={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                          error={errors.start_date}
                          onChange={(date) => {
                            setValue("start_date", date);
                            setStartDate(date);
                            trigger(["start_date"]);
                          }}
                          disabled={submitLoading}
                        />
                        <div className="text-danger">{errors.start_date ? errors.start_date.message : ""}</div>
                      </Col>
                      <Col sm={12} md={6} lg={3} className="second-col">
                        <div className="dashboard-datepicker">
                          <DatePicker
                            name="expiry_date"
                            placeholder="Discount end date"
                            value={endDate}
                            style={{ padding: 0 }}
                            className={`form-control digits ${errors.expiry_date ? "is-invalid" : ""}`}
                            showTime
                            format="dd-MM-yyyy HH:mm"
                            disabledDate={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return date < today;
                            }}
                            error={errors.expiry_date}
                            onChange={(date) => {
                              setValue("expiry_date", date);
                              setEndDate(date);
                              trigger(["start_date", "expiry_date"]);
                            }}
                            disabled={submitLoading}
                          />
                          <div className="text-danger">{errors.expiry_date ? errors.expiry_date.message : ""}</div>
                        </div>
                      </Col>
                      {[1].includes(discountTypeGlobe) && (
                        <Col sm={12} md={6} lg={3} className="second-col">
                          <Controller
                            name="buy_qty"
                            control={control}
                            render={({ field }) => (
                              <Input {...field} bsSize="md" type="number" placeholder="Buy Quantity*" invalid={!!errors.buy_qty} disabled={submitLoading}/>
                            )}
                          />
                          <div className="text-danger">{errors.buy_qty ? errors.buy_qty.message : ""}</div>
                        </Col>
                      )}

                      {[2].includes(discountTypeGlobe) && (
                        <>
                          <Col sm={12} md={6} lg={3} className="second-col">
                            <Select
                              placeholder={<div>Customer Spends</div>}
                              className="select2-filter"
                              isClearable
                              cacheOptions
                              options={buyQuantityOptions}
                              defaultValue={getValues("customer_spends") === 2 ? buyQuantityOptions[0] : buyQuantityOptions[1]}
                              onChange={(select) => {
                                const value = select ? select.value : null;
                                setValue("customer_spends", value);
                                trigger(["customer_spends"]);
                              }}
                              components={{
                                IndicatorSeparator: () => null,
                              }}
                              isDisabled={submitLoading}
                            />
                          </Col>
                          {getValues("customer_spends") === 2 ? (
                            <Col key="minimum_quantity" sm={12} md={6} lg={3} className="second-col">
                              <Controller
                                name="minimum_quantity"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      placeholder="Minimum Quantity"
                                      aria-label="Minimum Quantity"
                                      invalid={!!errors.minimum_quantity}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                              <div className="text-danger" style={{ color: "red" }}>
                                {errors.minimum_quantity ? errors.minimum_quantity.message : ""}
                              </div>
                            </Col>
                          ) : getValues("customer_spends") === 3 ? (
                            <Col key="minimum_amount" sm={12} md={6} lg={3} className="second-col">
                              <Controller
                                name="minimum_amount"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <InputGroupText>£</InputGroupText>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Minimum Amount"
                                      aria-label="Minimum Amount"
                                      invalid={!!errors.minimum_amount}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                              <div className="text-danger" style={{ color: "red" }}>
                                {errors.minimum_amount ? errors.minimum_amount.message : ""}
                              </div>
                            </Col>
                          ) : null}
                        </>
                      )}

                      {[1, 2].includes(discountTypeGlobe) && (
                        <Col sm={12} md={6} lg={3} className="second-col">
                          <Controller
                            name="get_qty"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                bsSize="md"
                                type="number"
                                placeholder="Get Quantity*"
                                invalid={!!errors.get_qty}
                                readOnly={status === 1}
                                disabled={submitLoading}
                              />
                            )}
                          />
                          <div className="text-danger">{errors.get_qty ? errors.get_qty.message : ""}</div>
                        </Col>
                      )}
                      {[3, 4].includes(discountTypeGlobe) && (
                        <>
                          <Col sm={12} md={6} lg={3} className="second-col">
                            <AsyncSelect
                              className="basic-single"
                              classNamePrefix="select"
                              isClearable={true}
                              defaultOptions
                              value={
                                discountValue.find((opt) => opt.value === watch("discount_value")) || null
                              }
                              placeholder="Discount Value"
                              loadOptions={loadOptions}
                              menuPortalTarget={document.body}
                              components={{
                                IndicatorSeparator: () => null,
                              }}
                              onChange={(value) => {
                                setValue("discount_value", value ? value.value : null);
                              }}
                              isDisabled={submitLoading}
                            />
                          </Col>

                          {getValues("discount_value") === 1 ? (
                            <Col key="amount" sm={12} md={6} lg={3} className="second-col">
                              <Controller
                                name="amount"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <InputGroupText>£</InputGroupText>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      step="any"
                                      placeholder="Amount"
                                      aria-label="Amount"
                                      invalid={!!errors.amount}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                              <div className="text-danger" style={{ color: "red" }}>
                                {errors.amount ? errors.amount.message : ""}
                              </div>
                            </Col>
                          ) : getValues("discount_value") === 2 ? (
                            <Col key="percentage" sm={12} md={6} lg={3} className="second-col">
                              <Controller
                                name="percentage"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      step="any"
                                      placeholder="Percentage"
                                      aria-label="Percentage"
                                      invalid={!!errors.percentage}
                                      disabled={submitLoading}
                                    />
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroup>
                                )}
                              />
                              <div className="text-danger" style={{ color: "red" }}>
                                {errors.percentage ? errors.percentage.message : ""}
                              </div>
                            </Col>
                          ) : null}
                        </>
                      )}

                      <Col sm={12} md={6} lg={3} className="second-col active-col">
                        <FormGroup className="d-flex align-items-center">
                          <div className="form-control pl-0 border-0 ">
                            <Controller
                              name="is_active"
                              control={control}
                              render={({ field }) => (
                                <Switch
                                  {...field}
                                  checked={field.value}
                                  onChange={(checked) => {
                                    field.onChange(checked);
                                  }}
                                  onColor="#7bf1a8"
                                  onHandleColor="#00a63e"
                                  handleDiameter={25}
                                  uncheckedIcon={false}
                                  checkedIcon={false}
                                  boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                  activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                  height={20}
                                  width={48}
                                  className="react-switch"
                                  id="material-switch"
                                  // disabled={id}
                                  disabled={submitLoading}
                                />
                              )}
                            />
                          </div>
                          <Label className="form-label mb-0 h6">Active</Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Card>
                  {/* Minimum Purchase Requirement */}
                  {[3, 4, 5].includes(watch("discount_type")) && (
                    <Card>
                      <CardBody className="section-minimum">
                        <div className="ribbon ribbon-clip ribbon-dark ribbon-left mb-5">Minimum purchase requirement</div>
                        <Row className="mt-5">
                          <Col sm={12}>
                            <Alert color="warning">
                              <b> Note: </b> You won't be able to update Minimum Purchase Requirement Type so choose carefully. Only the values can be
                              changed like amount or quantity.
                            </Alert>
                          </Col>
                        </Row>
                        <Row className="top-row">
                          <Col sm="12" className="discount-lists-minimum">
                            <Nav tabs className="border-tab">
                              {minimumPurchaseRequirement.map((item, index) => (
                                <NavItem key={item.id} className={`${watch("mpr_type") === item.id ? "flex-active" : ""}`}>
                                  <NavLink
                                    className={watch("mpr_type") === item.id ? "active" : ""}
                                    onClick={() => {
                                      if (submitLoading) return;
                                      setMinimumPurchaseStatus(index);
                                      setValue("mpr_type", item.id);
                                    }}
                                  >
                                    {item.name}
                                  </NavLink>
                                </NavItem>
                              ))}
                            </Nav>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm="12" style={{ paddingRight: 6 }}>
                            {watch("mpr_type") === 3 && (
                              <Controller
                                name="minimum_purchase"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <InputGroupText>£</InputGroupText>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      placeholder="Minimum Purchase Amount"
                                      aria-label="Minimum Purchase Amount"
                                      invalid={!!errors.minimum_purchase}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                            )}{" "}
                            {watch("mpr_type") === 2 && (
                              <>
                                <Controller
                                  name="minimum_items"
                                  control={control}
                                  render={({ field }) => (
                                    <InputGroup>
                                      <Input
                                        {...field}
                                        bsSize="md"
                                        type="number"
                                        step="1"
                                        min="0"
                                        placeholder="Minimum Quantity*"
                                        aria-label="Minimum Quantity"
                                        invalid={!!errors.minimum_items}
                                        disabled={submitLoading}
                                      />
                                    </InputGroup>
                                  )}
                                />
                                <div className="text-danger">{errors.minimum_items ? errors.minimum_items.message : ""}</div>
                              </>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  )}

                  {/* Discounted Value */}
                  {[2].includes(watch("discount_type")) && (
                    <Card>
                      <CardBody className="section-minimum">
                        <div className="ribbon ribbon-clip ribbon-dark ribbon-left mb-5">At a discounted value</div>
                        <Row className="top-row">
                          <Col sm="12" className="discount-lists-minimum">
                            <Nav tabs className="border-tab">
                              {discountedValueOptions.map((item, index) => (
                                <NavItem key={item.id} className={`${watch("discount_value") === item.id ? "flex-active" : ""}`}>
                                  <NavLink
                                    style={{ width: "100%" }}
                                    className={watch("discount_value") === item.id ? "active" : ""}
                                    onClick={() => {
                                      if(submitLoading) return;
                                      setDiscountedStatus(index);
                                      setValue("discount_value", item.id);
                                    }}
                                  >
                                    {item.name}
                                  </NavLink>
                                </NavItem>
                              ))}
                            </Nav>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm="12" style={{ paddingRight: 6 }}>
                            {watch("discount_value") === 1 && (
                              <Controller
                                name="discounted_amount"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <InputGroupText>£</InputGroupText>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Discounted Amount"
                                      aria-label="Discounted Amount"
                                      invalid={!!errors.discounted_amount}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                            )}{" "}
                            {watch("discount_value") === 2 && (
                              <Controller
                                name="discounted_percentage"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Discounted Percentage"
                                      aria-label="Discounted Percentage"
                                      invalid={!!errors.discounted_percentage}
                                      disabled={submitLoading}
                                    />
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroup>
                                )}
                              />
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  )}
                  {/* Discount Combination */}
                  {[1, 2, 3, 4, 5].includes(watch("discount_type")) && (
                    <Card>
                      <CardBody className="section-minimum">
                        <div className="ribbon ribbon-clip ribbon-dark ribbon-left mb-5">Combinations</div>
                        <Row style={{ flexDirection: "column" }} className="mt-2">
                          {watch("product_discount") && watch("order_discount") && (
                            <Alert color="warning">
                              <b> Some combinations can result in large discount </b>
                              <br /> Test a few combinations. If the total discount is too large, adjust which discounts can combine.
                            </Alert>
                          )}
                          <p>This product discount can be combined with:</p>
                        </Row>
                        <Row className="top-row">
                          <Col sm="3" className="discount-lists-minimum">
                            <Controller
                              name="product_discount"
                              control={control}
                              defaultValue={false}
                              render={({ field }) => (
                                <FormGroup check inline>
                                  <Input
                                    type="checkbox"
                                    {...field}
                                    bsSize="lg"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    disabled={submitLoading}
                                  />
                                  <Label check>Product discount</Label>
                                </FormGroup>
                              )}
                            />

                            {watch("product_discount") && (
                              <div style={{ color: "#616161" }}>
                                <p className="mb-0">
                                  <button
                                    type="button"
                                    className="px-0"
                                    style={{ color: "#1976d2", fontWeight: 500, background: "transparent" }}
                                    onClick={() => handleOpenDiscountModal("products")}
                                    disabled={submitLoading}
                                  >
                                    {discountState.settings?.combinedDiscounts?.product_discount?.count || 0} products discounts
                                  </button>{" "}
                                  are set to combine.
                                </p>
                                <p className="mt-0">If an item is eligible for multiple product discounts, only the largest will apply.</p>
                              </div>
                            )}
                          </Col>

                          <Col sm="3" className="discount-lists-minimum">
                            <Controller
                              name="order_discount"
                              control={control}
                              render={({ field }) => (
                                <FormGroup check inline>
                                  <Input
                                    type="checkbox"
                                    {...field}
                                    bsSize="lg"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    disabled={submitLoading}
                                  />
                                  <Label check>Order discount</Label>
                                </FormGroup>
                              )}
                            />

                            {watch("order_discount") && (
                              <div style={{ color: "#616161" }}>
                                <p className="mb-0">
                                  <button
                                    type="button"
                                    className="px-0"
                                    style={{ color: "#1976d2", fontWeight: 500, background: "transparent" }}
                                    onClick={() => handleOpenDiscountModal("order")}
                                    disabled={submitLoading}
                                  >
                                    {discountState.settings?.combinedDiscounts?.order_discount?.count || 0} orders discounts
                                  </button>{" "}
                                  are set to combine.
                                </p>
                                <p className="mt-0">Order discounts are applied after any eligible product discounts.</p>
                              </div>
                            )}
                          </Col>
                          <Col sm="3" className="discount-lists-minimum">
                            <Controller
                              name="shipping_discount"
                              control={control}
                              render={({ field }) => (
                                <FormGroup check inline>
                                  <Input
                                    type="checkbox"
                                    {...field}
                                    bsSize="lg"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    disabled={submitLoading}
                                  />
                                  <Label check>Shipping discount</Label>
                                </FormGroup>
                              )}
                            />

                            {watch("shipping_discount") && (
                              <div style={{ color: "#616161" }}>
                                <p className="mb-0">
                                  <button
                                    type="button"
                                    className="px-0"
                                    style={{ color: "#1976d2", fontWeight: 500, background: "transparent" }}
                                    onClick={() => handleOpenDiscountModal("shipping")}
                                    disabled={submitLoading}
                                  >
                                    {discountState.settings?.combinedDiscounts?.shipping_discount?.count || 0} shipping discounts
                                  </button>{" "}
                                  are set to combine.
                                </p>
                                <p className="mt-0">If an order is eligible for multiple shipping discounts, only the largest will apply.</p>
                              </div>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  )}
                  {/* Country Section */}
                  {[5].includes(watch("discount_type")) && (
                    <Card>
                      <CardBody className="section-minimum">
                        <div className="ribbon ribbon-clip ribbon-dark ribbon-left mb-5">Countries</div>
                        <Row style={{ flexDirection: "column" }} className="mt-2">
                          <Col sm="12" className="discount-lists-minimum">
                            <Nav tabs className="border-tab">
                              {countryeOptions.map((item, index) => (
                                <NavItem key={item.id} className={`${watch("country_status") === item.id ? "flex-active" : ""}`}>
                                  <NavLink
                                    className={watch("country_status") === item.id ? "active" : ""}
                                    onClick={() => {
                                      if (submitLoading) return; 

                                      let obj = {
                                        props: { isOpen: true },
                                      };
                                      setValue("country_status", item.id);
                                      seCountryStatus(index);
                                      if (watch("country_status") === 2) {
                                        dispatch(openCountryDialog(obj));
                                      }
                                    }}
                                  >
                                    {item.name}
                                  </NavLink>
                                </NavItem>
                              ))}
                            </Nav>
                          </Col>
                        </Row>

                        <ShippingRates selectedCountries={getValues("shipping_countries")} />

                        <Row className="mt-3 mb-2">
                          <Col sm="12">
                            <p style={{ fontSize: "0.9rem", fontWeight: 500 }}>Shipping rates</p>
                          </Col>
                        </Row>

                        <Row className="mb-2">
                          <Col sm="12">
                            <Controller
                              name="shipping_rates_checked"
                              control={control}
                              render={({ field }) => (
                                <FormGroup check inline>
                                  <Input
                                    {...field}
                                    type="checkbox"
                                    bsSize="lg"
                                    checked={field.value}
                                    onChange={(checked) => {
                                      field.onChange(checked);
                                    }}
                                    disabled={submitLoading}
                                  />
                                  <Label check>Exclude shipping rates over a certain amount</Label>
                                </FormGroup>
                              )}
                            />
                          </Col>
                        </Row>

                        {watch("shipping_rates_checked") && (
                          <Row>
                            <Col sm="12">
                              <Controller
                                name="shipping_rates"
                                control={control}
                                render={({ field }) => (
                                  <InputGroup>
                                    <InputGroupText>£</InputGroupText>
                                    <Input
                                      {...field}
                                      bsSize="md"
                                      type="number"
                                      placeholder="Shipping Rate"
                                      aria-label="Shipping Rate"
                                      invalid={!!errors.shipping_rates}
                                      disabled={submitLoading}
                                    />
                                  </InputGroup>
                                )}
                              />
                            </Col>
                          </Row>
                        )}
                      </CardBody>
                    </Card>
                  )}

                  <Card>
                    <CardBody className={[4, 5].includes(watch("discount_type")) ? `section-minimum p-0` : `section-minimum`}>
                      {![4, 5].includes(status) && (
                        <>
                          <div className="ribbon ribbon-clip ribbon-dark ribbon-left mb-5">Products</div>
                          <Row className="mt-3 g-3">
                            <Col sm={status === 2 ? 3 : 6} >
                              <Input type="text" placeholder="Search Products" name="products" onChange={handleSearchChange} value={searchTerm} disabled={submitLoading}/>
                            </Col>
                            <Col md={status === 2 ? 2 : 5} sm={status === 2 ? 2 : 5} xs={status === 2 ? 2 : 5}>
                              <Input type="text" placeholder="Search by SKU" name="products" onChange={handleSearchSkuChange} value={searchSkuTerm} disabled={submitLoading}/>
                            </Col>
                            <Col
                              sm={status === 2 ? 1 : 1}
                              style={status === 2 ? { paddingRight: 0 } : {}}
                            >
                              <Button
                                color="primary"
                                style={status === 2 ? { padding: "6px 0 9px 6px", width : "100%" } : { padding: "6px 0 9px 6px", width : "100%" }}
                                onClick={() => {
                                  setIsGet(false);
                                  let obj = {
                                    props: { isOpen: true },
                                  };
                                  dispatch(openProductsDialog(obj));
                                }}
                              >
                                Browse
                              </Button>
                            </Col>

                            {status === 2 && (
                              <>
                                <Col md={3}>
                                  <Input
                                    type="text"
                                    placeholder="Search Products"
                                    name="products"
                                    onChange={handleGetSearchChange}
                                    value={getSearchTerm}
                                    disabled={submitLoading}
                                  />
                                </Col>
                                <Col md={2}>
                                  <Input
                                    type="text"
                                    placeholder="Search By SKU"
                                    name="sku"
                                    onChange={handleGetSearchSkuChange}
                                    value={getSearchSkuTerm}
                                    disabled={submitLoading}
                                  />
                                </Col>
                                <Col md={1} style={status === 2 ? { paddingLeft: 0 } : {}}>
                                  <Button
                                    color="primary"
                                    style={status === 2 ? { padding: "6px 0 9px 6px",width : "100%" } : {}}
                                    onClick={() => {
                                      setGetSearchTerm("");
                                      setIsGet(true);
                                      let obj = {
                                        props: { isOpen: true },
                                      };
                                      dispatch(openProductsDialog(obj));
                                    }}
                                    disabled={submitLoading}
                                  >
                                    Browse
                                  </Button>
                                </Col>
                              </>
                            )}

                            <Col xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
                              <Row>
                                <Col xs={12}>
                                  <ProductTable getValues={getValues} control={control} discountType={status} submitLoading={submitLoading}/>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </>
                      )}

                      <Button
                        color="primary"
                        type="submit"
                        style={{ float: "right", marginTop: 16, marginBottom: 16, marginRight: [4, 5].includes(watch("discount_type")) ? 20 : 0 }}
                        disabled={
                          Object.keys(errors).length > 0 ||
                          submitLoading ||
                          (watch("discount_type") === 1 && getValues("buy_products").length === 0) ||
                          (watch("discount_type") === 2 && (getValues("buy_products").length === 0 || getValues("get_products").length === 0)) ||
                          (watch("discount_type") === 3 && getValues("buy_products").length === 0)
                        }
                      >
                        {submitLoading ? (id ? <span><i className="fa fa-spinner fa-spin fa-lg spinner mr-1" /> Updating </span> : <span><i className="fa fa-spinner fa-spin fa-lg spinner mr-1" /> Saving </span>) : (id ? "Update" : "Save")}
                      </Button>
                    </CardBody>
                  </Card>
                </Form>
              </div>
            </div>
          </>
        )}
      </Container>

      <ModalFormSubmit
        index={2}
        defaultValues={defaultValuesCountry}
        schema={schemaCountry}
        onSubmitForm={onSubmitCountryForm}
        reset={resetCountry}
        formState={formCountry}
        handleSubmit={handleSubmitCountry}
        title="Countries"
        selector="selectedCountries"
        dialog={({ discount }) => discount.discount.addCountryDialog}
        closeDialog={closeLookDialog}
        buttonDisable={buttonDisable}
        size="lg"
      >
        <CountriesModalWindow
          initialSelectedCountries={getValues("shipping_countries")}
          append={appendCountry}
          watch={watchCountry}
          setValue={setCountryValues}
          control={controlCountry}
          errors={errorsCountry}
          reset={resetCountry}
          getValues={getCountryValues}
          open={discountState.addCountryDialog.props.isOpen}
        />
      </ModalFormSubmit>

      <CommonModal modalProps={discountState.discountDialogs} closeDialog={closeDiscountWindowDialog} size="lg">
        <DiscountModal closeDialog={closeDiscountWindowDialog} />
      </CommonModal>

      <DiscountProductSearchWindow
        modalProps={discountState.productsDialog}
        onClose={closeProductWindowDialog}
        size="lg"
        orderGetValues={getValues}
        initialSearchTerm={searchTerm}
        initialSkuSearchTerm={searchSkuTerm}
        initialGetSearchTerm={getSearchTerm}
        initialGetSearchSkuTerm={getSearchSkuTerm}
        onAddProduct={handleAddProduct}
        discountType={watch("discount_type")}
        buyProducts={getValues("buy_products")}
        getProducts={getValues("get_products")}
        getValues={getValues}
        setValue={setValue}
        onClearValue={clearValues}
        isGet={isGet}
      />
    </Fragment>
  );
}

export default withReducer([{ discount: discountReducer }])(DiscountForm);
